/**
 * One task: what it is, when it is due, the time booked against it and the
 * conversation about it.
 *
 * Was three bordered boxes with a shadcn `<Select>` for the status. It is the
 * CRM's cards now, laid out the way `ProjectOverview` lays out a record: the
 * conversation on the left because it is the part that grows, the facts and the
 * timesheet stacked on the right. The two columns are `items-start`, so an empty
 * timesheet is a short card rather than a column-high blank - the first draft of
 * this screen stretched both cards to the same height and left a hole.
 *
 * The facts card is `TaskFacts` next door: it edits the task now, and a form
 * lives better beside this page than inside the file that owns the comments.
 *
 * On a phone there is one column, and the two above are woven into it rather
 * than stacked: description, facts, checklist, comments, timesheet, files.
 * Stacking them put the due date and the billing tick under the whole
 * conversation AND under the comment box, so the answer to "when is this due"
 * was two screens of scrolling away. See `.crm-weave` in index.css - the cards
 * carry the order, the grid dissolves the two wrappers at that width.
 *
 * EVERY card here needs a `crm-w*` number, and a card slipped into the MIDDLE
 * means renumbering the ones below it rather than taking the next free digit.
 * An unnumbered card computes `order: 0` and sorts above all of them, landing at
 * the top of the phone layout above the thing the page is about - and a card
 * that ties with another leaves the order to markup, which is nobody's decision.
 * Neither shows up on desktop and neither fails a typecheck.
 *
 * The head carries the placing of the task: which project it belongs to, which
 * PHASE of that project, and who is on it. The phase is here rather than only in
 * the facts card because a milestone name means nothing without the project name
 * beside it, and because two thirds of the tasks that arrived from the old
 * system carry a phase id that no screen in this product had ever drawn. The
 * faces are drawn only when somebody is actually on the task: every imported
 * task is on nobody, and a row of "no owner" marks on every head would be a
 * badge that means nothing. The facts card is where both are CHANGED.
 *
 * It also carries one thing the facts card cannot: whether the customer sees
 * this task at all. The tick box that decides it lives in the right-hand column,
 * and a task that is hidden from the client is not a detail you want somebody to
 * discover after they have read, commented on and closed the page. So the state
 * is said in words at the top - but only when the answer is the unusual one. A
 * badge on every visible task would be a badge that means nothing.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, FolderKanban, LifeBuoy, Loader2, MessageSquare,
  Milestone as MilestoneIcon, Trash2,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  addTaskComment, deleteTask, getProject, getTask, updateTask,
  type ChecklistItem, type Milestone, type Task, type TaskComment, type TaskStatus, type TimeEntry,
} from "../../../lib/projects";
import {
  Avatar, Btn, Card, CardHead, EmptyState, MenuField, PageHead, StatusPill, fmtDateTime,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { useCrmUser } from "../../shell/CrmUser";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { EntityFiles } from "../_shared/EntityFiles";
import { UserAvatars } from "../_shared/UserChips";
import { TASK_STATUSES } from "../projects/status";
import { TimerButton, useTimers } from "../../timer";
import { TaskChecklist } from "./TaskChecklist";
import { TaskFacts } from "./TaskFacts";
import { TaskTimesheet } from "./TaskTimesheet";

type FullTask = Task & {
  comments: TaskComment[];
  timeEntries: TimeEntry[];
  /** Required here, unlike on `Task`, because the DETAIL endpoint always sends
   *  it: the card below therefore never has to tell "no boxes" from "the boxes
   *  have not arrived", which are different sentences and only one of them is
   *  worth a spinner. */
  checklist: ChecklistItem[];
};

/**
 * A card body's padding.
 *
 * `crm-card` is a surface and nothing else - the radius and the shadow, no
 * padding - so anything that is not a `crm-card-head` or a `crm-rows` has to say
 * where its edges are. Leaving it off puts the text hard against the card's
 * corner, which is what the first draft of this screen did.
 */
const BODY = "px-6 pb-5 pt-3";

export function TaskDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Both read here rather than further down: this component returns early while
  // the task is loading, and a hook called after that returns on some renders
  // and not others.
  const user = useCrmUser();
  const timers = useTimers();
  const [task, setTask] = useState<FullTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  /** The project's phases: `null` until they are known, and `null` again if they
   *  cannot be read. See the effect below for why the failure is not `[]`. */
  const [milestones, setMilestones] = useState<Milestone[] | null>(null);

  /**
   * Re-read the task.
   *
   * `quiet` decides whether the page is blanked while it happens, and the
   * distinction is the difference between arriving and staying. Opening a task
   * has nothing to show yet, so the spinner is honest. Every OTHER call is a
   * refresh after the reader changed something on the page they are looking at
   * - a comment posted, a clock stopped, an hour logged or deleted - and
   * replacing the whole screen with a spinner for those scrolls them back to
   * the top of a page they were in the middle of. The timesheet card sits near
   * the bottom, so logging two hours in a row meant scrolling down twice.
   */
  const load = useCallback(
    (quiet = false) => {
      if (!id) return;
      if (!quiet) setLoading(true);
      getTask(id)
        .then(setTask)
        .finally(() => setLoading(false));
    },
    [id],
  );

  useEffect(() => {
    load();
  }, [load]);

  // The hours a stopped clock produced belong in the card below, and nothing
  // else on this page would fetch them: the shell's timer list knows the clock
  // is gone, but the task (and its `timeEntries`) was read before it stopped.
  // Without this, pressing stop makes the running line disappear and puts
  // nothing in its place, which reads as "the time was lost".
  //
  // Keyed on WHICH clocks are on this task rather than on how many. A stop
  // followed immediately by a start would leave the count unchanged, and the
  // recorded hours would stay invisible until a reload.
  const runningKey = (timers?.onTask(id ?? "") ?? []).map((r) => r.id).join(",");
  const lastRunningKey = useRef(runningKey);
  useEffect(() => {
    const previous = lastRunningKey.current;
    lastRunningKey.current = runningKey;
    // Only when something that WAS running is no longer there. A clock starting
    // adds nothing to the task's own record, so reloading for that would be a
    // request per start for no change on screen.
    if (previous && previous !== runningKey) load(true);
  }, [runningKey, load]);

  // The phases of the project this task sits on, for the line in the head and
  // for the field in the facts card. They come from the PROJECT because the task
  // endpoint carries `milestoneId` and nothing else about it - the id itself is
  // not a name anybody can read.
  //
  // A failure leaves this null rather than an empty list, and the difference
  // matters twice over: a phase that cannot be named is drawn as nothing instead
  // of as "no phase", which is a different and false statement, and the facts
  // card sends `milestoneId` only when it has a list - so an unreadable project
  // can never end up unfiling a task nobody touched.
  const projectId = task?.projectId ?? null;
  useEffect(() => {
    setMilestones(null);
    if (!projectId) return;
    let alive = true;
    getProject(projectId)
      .then((p) => {
        if (alive) setMilestones(p.milestones);
      })
      .catch(() => {
        /* Left unknown on purpose - see above. */
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (!task) {
    return <EmptyState icon={MessageSquare} title={t("tasks.notFound")} />;
  }

  async function postComment(e: React.FormEvent) {
    e.preventDefault();
    if (!task || !comment.trim()) return;
    setBusy(true);
    try {
      await addTaskComment(task.id, comment.trim());
      setComment("");
      load(true);
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(s: TaskStatus) {
    if (!task) return;
    // Optimistic, then written. The server also stamps `completedAt` on "done",
    // which is why the response is taken rather than the local guess kept.
    setTask((prev) => (prev ? { ...prev, status: s } : prev));
    const next = await updateTask(task.id, { status: s });
    setTask((prev) => (prev ? { ...prev, ...next } : prev));
  }

  // The phase, once the project has answered. `undefined` covers both "the list
  // has not arrived" and "the id names nothing we can find", and both are drawn
  // as nothing: naming a milestone we cannot resolve would be inventing one.
  const phase = task.milestoneId ? milestones?.find((m) => m.id === task.milestoneId) : undefined;

  return (
    <PopHost>
      {/* A real Link, not a button that navigates: back is a place, and the
          browser's own middle-click and history belong to it. */}
      <Link to="/tasks" className="crm-mut mb-3 inline-flex items-center gap-1 text-[12.5px] hover:text-foreground">
        <ChevronLeft className="size-3.5" />
        {t("tasks.back")}
      </Link>

      <PageHead
        title={task.title}
        sub={
          /* Where the task sits and who is on it: the project, the phase inside
             it, the faces - and, only when it is hidden, who cannot see it.
             Muted rather than a warning tone: hiding a task is a decision
             somebody made on purpose, not a fault to fix. */
          <span className="flex flex-wrap items-center gap-2.5">
            {task.project ? (
              <Link to={`/projects/${task.project.id}`} className="inline-flex items-center gap-1.5 hover:underline">
                <FolderKanban className="size-3.5" />
                {task.project.name}
              </Link>
            ) : (
              t("tasks.noProject")
            )}
            {/* Not a link: a milestone has no page of its own, and a name that
                looks clickable and is not is worse than plain text. The icon
                and the title say what the word is, since a bare second name
                beside the project reads as part of it. */}
            {phase ? (
              <span className="inline-flex items-center gap-1.5" title={t("projects.milestone")}>
                <MilestoneIcon className="size-3.5" />
                {phase.name}
              </span>
            ) : null}
            {/* Only when somebody is actually on it. `UserAvatars` draws the
                "nobody" mark for an empty list, which is right in a table cell
                that has to hold its row open and wrong here - it would put a
                stray hyphen in the head of every task in the register, because
                on the day this shipped not one of them had an owner. */}
            {task.assignees.length ? <UserAvatars ids={task.assignees} size={20} /> : null}
            {/* Where the work came from, on the tasks that came from somewhere.
                A link, unlike the milestone beside it: a ticket IS a page, and
                the number is what the task is called in the customer's own
                e-mail about it. Only drawn when the relation resolved - a ticket
                that has been deleted clears the column, so a task with no badge
                here is a task with no origin, not one whose origin failed to
                load. */}
            {task.ticket ? (
              <Link
                to={`/support/${task.ticket.id}`}
                className="inline-flex items-center gap-1.5 hover:underline"
                title={task.ticket.subject}
              >
                <LifeBuoy className="size-3.5" />
                <span className="crm-tnum">{task.ticket.number}</span>
              </Link>
            ) : null}
            {task.visibleToClient ? null : (
              <StatusPill tone="mut">{t("projects.hiddenFromClient")}</StatusPill>
            )}
          </span>
        }
        actions={
          <>
            {/* First, and the only action here that is about doing the work
                rather than about filing it. It draws nothing for an operator
                who cannot run a clock. */}
            <TimerButton taskId={task.id} />
            <MenuField
              value={task.status}
              onChange={(s) => void changeStatus(s)}
              label={t("projects.statusCol")}
              options={TASK_STATUSES.map((s) => ({ value: s, label: t(`tasks.status.${s}`) }))}
            />
            <Btn tone="danger" onClick={() => setConfirmDel(true)}>
              <Trash2 className="size-4" />
              {t("common.delete")}
            </Btn>
          </>
        }
      />

      {/* items-start: the timesheet is usually the shorter card, and stretching
          it to the height of the conversation next to it is a column of nothing.

          crm-weave: below 900px the two columns become one, and a plain fold
          would put the facts and the timesheet under the whole conversation.
          The `crm-w*` classes below say what the single-column order is, which
          is the reading order of the record: what it is, where it stands, what
          was said, what it cost. */}
      <div className="crm-cols-ab crm-weave items-start">
        <div className="crm-weave-col">
          {/* The task's own text first: it is what the comments below are about,
              and putting it under both columns (the first draft) orphaned it. */}
          {task.description ? (
            <Card className={cn(BODY, "crm-w1 whitespace-pre-wrap pt-5 text-[13.25px] leading-relaxed")}>
              {task.description}
            </Card>
          ) : null}

          {/* The steps, between the task's text and the conversation about it.
              They are the plan for the work rather than a discussion of it, so
              they sit above the comments in both layouts - and on a phone the
              weave carries the same order (`crm-w3`), which is the whole reason
              the number is here and not left to fall out of the markup.

              Note what this card does NOT do: ticking every box does not finish
              the task. `TaskChecklist` and `checklist.routes.ts` both write out
              why at length. */}
          <TaskChecklist className="crm-w3" taskId={task.id} items={task.checklist} />

          <Card className="crm-w4">
            <CardHead
              title={
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="size-3.5" />
                  {t("tasks.comments")}
                </span>
              }
            />

            <div className={BODY}>
              {task.comments.length === 0 ? (
                <p className="crm-mut text-[13px]">{t("tasks.noComments")}</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {task.comments.map((c) => (
                    <li key={c.id} className="flex gap-2.5">
                      <Avatar name={c.author} />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-2">
                          <span className="crm-prow-n">{c.author}</span>
                          <span className="crm-prow-s crm-tnum">{fmtDateTime(c.createdAt)}</span>
                        </span>
                        <p className="mt-0.5 whitespace-pre-wrap text-[13px]">{c.body}</p>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={postComment} className="mt-3 flex flex-col gap-2.5">
                <textarea
                  className="crm-area"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("tasks.commentPlaceholder")}
                  rows={2}
                />
                <span className="flex justify-end">
                  <Btn type="submit" tone="dark" disabled={busy || !comment.trim()}>
                    {busy ? <Loader2 className="size-4 animate-spin" /> : <MessageSquare className="size-4" />}
                    {t("tasks.addComment")}
                  </Btn>
                </span>
              </form>
            </div>
          </Card>
        </div>

        <div className="crm-weave-col">
          {/* The facts, so the right column carries the record rather than one
              near-empty timesheet. Its own file: the card is a form now, and a
              form inside the page that owns the comments is two screens in one. */}
          <TaskFacts
            className="crm-w2"
            task={task}
            milestones={milestones}
            onSaved={(next) => setTask((prev) => (prev ? { ...prev, ...next } : prev))}
          />

          {/* The task's own timesheet, and the three things that can be done to
              it. Its own file: the card grew a menu, an add dialog and a delete
              confirm, and this page already owns the head, the comments, the
              status menu and its own delete. */}
          <TaskTimesheet
            className="crm-w5"
            taskId={task.id}
            taskTitle={task.title}
            projectId={task.projectId}
            entries={task.timeEntries}
            onChange={() => load(true)}
          />

          {/* The task's files: the SAME card the project page's files tab draws,
              handed this task as its owner rather than copied. `CrmAttachment`
              is one polymorphic table, so these are the same rows read with a
              different `entity`, and a second component would be a second place
              for the "download by row id, never by storage key" rule to live.
              Only the wording is this page's, because "no files on this task
              yet" is a different sentence from the project's.

              `crm-w6` is not decoration. Every card inside a `.crm-weave` needs
              an order class: an unnumbered one computes `order: 0` and sorts
              ABOVE all of them on a phone, so a files card added without a
              number would land over the task's own description. Appended rather
              than slipped in between, so the five cards already here keep their
              numbers - files are supporting material for the record above them,
              which is the same place they sit in the project page's tab strip.
              It is 6 and not 5 because the checklist took a place in the middle
              and pushed the timesheet down; a tie would be settled by markup
              order, which is not a decision anybody wrote down. */}
          <EntityFiles
            className="crm-w6"
            entity="task"
            entityId={task.id}
            title={t("tasks.files")}
            emptyBody={t("tasks.noFiles")}
          />
        </div>
      </div>

      {confirmDel ? (
        <ConfirmModal
          title={t("tasks.deleteTitle")}
          body={t("tasks.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await deleteTask(task.id);
            navigate("/tasks");
          }}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}
    </PopHost>
  );
}
